import { useState, useLayoutEffect } from 'react';

const EMPTY_VIEWPORT = { height: null, top: 0, isKeyboardOpen: false };

/** Live visible rectangle. Shared by both mobile chat views (including Android).
 * The keyboard is already excluded by visualViewport: never subtract it twice.
 * `enabled` keeps desktop and inactive conversations out of this layout loop.
 */
export function useIOSViewport(enabled = true, settleOnEnable = false) {
  const [viewport, setViewport] = useState(EMPTY_VIEWPORT);

  useLayoutEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const vv = window.visualViewport;
    let frame = null;
    let settleTimer = null;
    const activationTimers = [];
    let fullHeight = 0;
    let lastWidth = 0;
    let keyboardOpen = false;

    const update = () => {
      frame = null;
      // Pinch zoom is not a keyboard transition. Leave zoom to the browser.
      if (vv && Math.abs((vv.scale || 1) - 1) > 0.02) return;
      const height = vv?.height || window.innerHeight;
      if (!Number.isFinite(height) || height <= 0) return;
      const width = window.innerWidth;
      const layoutHeight = Math.max(window.innerHeight, document.documentElement.clientHeight);
      if (width !== lastWidth) {
        fullHeight = layoutHeight;
        lastWidth = width;
      }
      const active = document.activeElement;
      const editable = !!active && (active.isContentEditable ||
        /^(TEXTAREA|INPUT)$/.test(active.tagName));
      const gap = Math.max(fullHeight, layoutHeight) - height;
      keyboardOpen = (editable || keyboardOpen) && gap > 100;
      if (!keyboardOpen && !editable) fullHeight = layoutHeight;
      // WebKit can briefly retain an old offset after keyboard dismissal.
      const top = Math.max(0, Math.min(vv?.offsetTop || 0, layoutHeight - height));
      const next = { height, top, isKeyboardOpen: keyboardOpen };
      setViewport(prev => prev.height === height && prev.top === top &&
        prev.isKeyboardOpen === keyboardOpen ? prev : next);
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };
    const settle = () => {
      schedule();
      window.clearTimeout(settleTimer);
      // A focus event can precede the final keyboard animation frame.
      settleTimer = window.setTimeout(schedule, 250);
    };

    update();
    // Returning from a fixed-body conversation can change WebKit's visible
    // rectangle without a resize event. Sample the short unlock transition;
    // do not scroll the page or keep polling after it has settled.
    if (settleOnEnable) {
      schedule();
      [100, 300, 600].forEach(delay => {
        activationTimers.push(window.setTimeout(schedule, delay));
      });
    }
    vv?.addEventListener('resize', schedule);
    vv?.addEventListener('scroll', schedule);
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', settle);
    window.addEventListener('pageshow', settle);
    document.addEventListener('focusin', settle);
    document.addEventListener('focusout', settle);
    document.addEventListener('visibilitychange', settle);
    return () => {
      vv?.removeEventListener('resize', schedule);
      vv?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', settle);
      window.removeEventListener('pageshow', settle);
      document.removeEventListener('focusin', settle);
      document.removeEventListener('focusout', settle);
      document.removeEventListener('visibilitychange', settle);
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer);
      activationTimers.forEach(id => window.clearTimeout(id));
    };
  }, [enabled, settleOnEnable]);

  return enabled ? viewport : EMPTY_VIEWPORT;
}
